import cn from 'classnames';
import { IconArrow } from '@/assets/icons/IconArrow';
import { Button } from '@/ui-components/atoms/Button/Button';
import { Text } from '@/ui-components/atoms/Text/Text';
import {
    AppDownloadButtons,
    DownloadButtonProps,
} from '@/ui-components/molecules/AppDownloadButtons/AppDownloadButtons';
import { SnapshotAlertBanner, SnapshotAlertBannerProps } from './SnapshotAlertBanner';
import { SignInMethod, SignInMethodOption } from './SnapshotUser.types';
import styles from './snapshotpopover.module.scss';

interface SnapshotUserChooseSignInMethodProps {
    appdownloadItems: DownloadButtonProps[];
    methodOptions: SignInMethodOption[];
    headingId: string;
    notificationBanner?: SnapshotAlertBannerProps;
    onBack: () => void;
    onContinue?: () => void | Promise<void>;
    onSelectMethod: (method: SignInMethod) => void;
    selectedMethod: SignInMethod;
}

export function SnapshotUserChooseSignInMethod({
    appdownloadItems,
    methodOptions,
    headingId,
    notificationBanner,
    onBack,
    onContinue,
    onSelectMethod,
    selectedMethod,
}: SnapshotUserChooseSignInMethodProps): React.JSX.Element {
    return (
        <div className={styles.snapshotUserFlow}>
            <div className={styles.snapshotSubscreenTopBar}>
                <button
                    aria-label="Go back"
                    className={styles.snapshotBackButton}
                    onClick={onBack}
                    type="button"
                >
                    <IconArrow />
                </button>
            </div>

            <div className={styles.snapshotDivider}>
                <div className={styles.snapshotDividerLine} />
            </div>

            <div className={styles.snapshotSubscreenMain}>
                <div className={styles.snapshotSubscreenSection}>
                    <Text tag="h3" className={styles.snapshotAnonymousTitle} id={headingId}>
                        How would you like to sign in?
                    </Text>

                    {notificationBanner && (
                        <SnapshotAlertBanner
                            {...notificationBanner}
                            layout="inline"
                        />
                    )}

                    <fieldset className={styles.snapshotMethodOptions}>
                        <legend className="show-for-sr">Sign in methods</legend>
                        {methodOptions.map((option) => {
                            const isSelected = selectedMethod === option.value;

                            return (
                                <label
                                    className={styles.snapshotMethodOption}
                                    key={option.value}
                                >
                                    <input
                                        checked={isSelected}
                                        className={styles.snapshotMethodInput}
                                        name="snapshot-sign-in-method"
                                        onChange={() => onSelectMethod(option.value)}
                                        type="radio"
                                        value={option.value}
                                    />
                                    <span className={styles.snapshotMethodRadio}>
                                        <span
                                            className={cn(styles.snapshotMethodRadioOuter, {
                                                [styles.snapshotMethodRadioOuterSelected]: isSelected,
                                            })}
                                        >
                                            {isSelected && (
                                                <span className={styles.snapshotMethodRadioInner} />
                                            )}
                                        </span>
                                    </span>

                                    <span className={styles.snapshotMethodCopy}>
                                        <Text tag="span" className={styles.snapshotMethodLabel}>
                                            {option.label}
                                        </Text>
                                        {option.description && (
                                            <Text tag="span" className={styles.snapshotMethodDescription}>
                                                {option.description}
                                            </Text>
                                        )}
                                    </span>
                                </label>
                            );
                        })}
                    </fieldset>

                    {selectedMethod === 'text' && (
                        <Text tag="p" className={styles.snapshotMethodLegal}>
                            By tapping &quot;Continue&quot; you agree to receive a one-time
                            automated message at the phone number selected for the purpose of
                            receiving a verification code. Message and data rates may apply.
                        </Text>
                    )}
                </div>

                <div className={styles.snapshotMethodActions}>
                    <Button
                        ariaLabel="Continue"
                        fitting="fill-container"
                        onClick={onContinue}
                        size="small"
                        type="button"
                        variant="primary"
                    >
                        Continue
                    </Button>
                </div>
            </div>

            <div className={styles.snapshotDivider}>
                <div className={styles.snapshotDividerLine} />
            </div>

            <div className={styles.snapshotAnonymousFooter}>
                <Text tag="h4" className={styles.snapshotAppHeading}>
                    Get the SiriusXM app
                </Text>
                <AppDownloadButtons
                    items={appdownloadItems}
                    className={styles.snapshotDownloadButtons}
                    appleClassName={styles.snapshotStoreBadge}
                    androidClassName={styles.snapshotStoreBadge}
                />
            </div>
        </div>
    );
}
